import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../helpers/axios";
import { Chart } from "react-chartjs-2";
import "./LoanRecyclingCard.css";

const TIER_COLORS = {
  Critical: "#d1435b",
  High: "#c77700",
  Watch: "#2563eb",
  Low: "#0f9d58",
};

const TIER_TAG_CLASS = {
  Critical: "lrc-t-red",
  High: "lrc-t-amber",
  Watch: "lrc-t-blue",
  Low: "lrc-t-green",
};

// Colors keyed off each month's own computed `flag` (V1-02-FORMULA-SPEC.md
// section 7.3) — never an arbitrary index/position in the series.
const FLAG_COLORS = {
  loan_to_repay: "#d1435b",
  watch: "#c77700",
  none: "#a855f7",
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MAX_SCORE = 16; // sum of all point weights in V1-02-FORMULA-SPEC.md section 5 — used only to size the runway bar
const MICRO_EMI_THRESHOLD = 1000; // avgEmi below this reads as a "micro" obligation — same tunable-constant pattern as BUFFER_FLOOR/LENDER_MANY in the backend spec

function formatMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
}

function formatCurrency(amt) {
  if (amt == null || isNaN(amt)) return "—";
  return "₹" + Intl.NumberFormat("en-IN").format(Math.round(amt));
}

function formatRunwayBand(runway) {
  if (!runway || runway.months == null) return { big: "—", unit: "", band: "" };
  if (runway.months < 1) {
    const low = Math.round((runway.low ?? runway.months) * 30);
    const high = Math.round((runway.high ?? runway.months) * 30);
    return { big: "< 1", unit: "month at current burn", band: `Band ~ ${low}-${high} days` };
  }
  const low = (runway.low ?? runway.months).toFixed(1);
  const high = (runway.high ?? runway.months).toFixed(1);
  return { big: runway.months.toFixed(1), unit: "months at current burn", band: `Band ~ ${low}-${high} months` };
}

const INSIGHT_ICONS = ["lrc-t-red", "lrc-t-amber", "lrc-t-blue", "lrc-t-green"];

/**
 * Loan-Recycling Risk card — fetches GET /bsi/loan-recycling/:userId/:appId,
 * which computes and caches the result lazily on first request. Deliberately
 * independent of BSI: it loads as soon as the Account Aggregator tab opens, for
 * both new and existing applications, whether or not BSI's "Run Analysis" has
 * ever been triggered. Read-only: never triggers analysis or deducts AI
 * credits. Visual language mirrors ai-mockups/bsi/aa-raw-analysis-mockup.html,
 * adapted to real computed data.
 */
export default function LoanRecyclingCard({ userId, appId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loanRecycling, setLoanRecycling] = useState(null);
  const [unavailableReason, setUnavailableReason] = useState(null);

  useEffect(() => {
    if (!userId || !appId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUnavailableReason(null);
    axiosInstance
      .get(`/bsi/loan-recycling/${userId}/${appId}`)
      .then((res) => {
        if (cancelled) return;
        const result = res?.data?.data ?? res?.data ?? null;
        if (result?.status === "unavailable") {
          setUnavailableReason(result.error || "Account Aggregator data not available yet");
          setLoanRecycling(null);
        } else {
          setLoanRecycling(result);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setLoanRecycling(null);
        } else {
          setError("Failed to load loan-recycling analysis");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, appId]);

  if (loading) {
    return (
      <div className="lrc-card">
        <div className="lrc-state-text">Loading loan-recycling analysis…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lrc-card">
        <div className="lrc-state-text lrc-state-error">{error}</div>
      </div>
    );
  }

  if (unavailableReason) {
    return (
      <div className="lrc-card">
        <div className="lrc-state-text">🔁 Loan-Recycling: {unavailableReason}</div>
      </div>
    );
  }

  if (!loanRecycling) {
    return null;
  }

  if (loanRecycling.status === "insufficient_data") {
    return (
      <div className="lrc-card">
        <div className="lrc-state-text">
          🔁 Loan-Recycling: not enough statement history to analyze (need 3+ complete months, found{" "}
          {loanRecycling.windowMonths ?? 0}).
        </div>
      </div>
    );
  }

  const {
    riskTier,
    score = 0,
    reasons = [],
    runway,
    signals = {},
    monthlySeries = [],
    lenders = [],
    assumptions,
    bureauContradiction,
  } = loanRecycling;

  const tierColor = TIER_COLORS[riskTier] || "#667085";
  const tierTagClass = TIER_TAG_CLASS[riskTier] || "";
  const runwayBand = formatRunwayBand(runway);
  const runwayBarPct = Math.max(6, Math.min(100, Math.round((score / MAX_SCORE) * 100)));
  const labels = monthlySeries.map((m) => formatMonth(m.month));

  const ratioPct = Math.round((signals.loanFundedRatio || 0) * 100);
  const ratioTagClass = ratioPct > 80 ? "lrc-t-red" : ratioPct > 50 ? "lrc-t-amber" : ratioPct > 0 ? "lrc-t-blue" : "lrc-t-green";
  const ratioTagLabel = ratioPct > 80 ? "Very High" : ratioPct > 50 ? "High" : ratioPct > 0 ? "Watch" : "Low";

  const allLenderNames = lenders.map((l) => l.name).filter(Boolean);
  const topLenderNames = allLenderNames.slice(0, 4);
  const lenderFoot = topLenderNames.length
    ? topLenderNames.join(", ") + (allLenderNames.length > topLenderNames.length ? "…" : "")
    : "no distinct lenders identified";
  // Full list shown on hover via the native title tooltip when the footer text is truncated.
  const lenderFootTitle = allLenderNames.length > topLenderNames.length ? allLenderNames.join(", ") : undefined;

  // Real, non-fabricated per-lender signals: the true max avgEmi in the list,
  // and a documented micro-EMI threshold. No trend/brand-grouping data exists
  // to support labels like "Growing"/"Stacked", so those cells stay blank.
  const maxLenderAvgEmi = lenders.length ? Math.max(...lenders.map((l) => l.avgEmi || 0)) : null;
  function lenderSignal(l) {
    if (maxLenderAvgEmi != null && l.avgEmi === maxLenderAvgEmi) {
      return { label: "Largest obligation", cls: "lrc-flag-red" };
    }
    if ((l.avgEmi || 0) < MICRO_EMI_THRESHOLD) {
      return { label: "Micro-EMI", cls: "lrc-flag-blue" };
    }
    return null;
  }

  const totals = monthlySeries.reduce(
    (acc, m) => {
      if (!m.complete) return acc;
      acc.salaryIn += m.salaryIn || 0;
      acc.emiOut += m.emiOut || 0;
      acc.loanIn += m.loanIn || 0;
      acc.disbursalCount += m.disbursalCount || 0;
      acc.trueNet += m.trueNet || 0;
      return acc;
    },
    { salaryIn: 0, emiOut: 0, loanIn: 0, disbursalCount: 0, trueNet: 0 },
  );

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } } },
  };

  const flowChartData = {
    labels,
    datasets: [
      {
        label: "Loan disbursals in",
        data: monthlySeries.map((m) => m.loanIn),
        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,.08)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "EMI paid out",
        data: monthlySeries.map((m) => m.emiOut),
        borderColor: "#d1435b",
        backgroundColor: "rgba(209,67,91,.08)",
        fill: true,
        tension: 0.3,
        borderWidth: 3,
      },
      {
        label: "Salary in",
        data: monthlySeries.map((m) => m.salaryIn),
        borderColor: "#0f9d58",
        borderDash: [5, 4],
        fill: false,
        tension: 0.3,
      },
    ],
  };

  const stackChartData = {
    labels,
    datasets: [
      {
        label: "New loans",
        data: monthlySeries.map((m) => m.disbursalCount),
        backgroundColor: monthlySeries.map((m) => FLAG_COLORS[m.flag] || FLAG_COLORS.none),
      },
    ],
  };

  const balChartData = {
    labels,
    datasets: [
      { label: "Month-end balance", data: monthlySeries.map((m) => m.endBalance), backgroundColor: "#c9b8f2" },
      { label: "Minimum balance", data: monthlySeries.map((m) => m.minBalance), backgroundColor: "#d1435b" },
    ],
  };

  return (
    <div className={`lrc-card ${isOpen ? "lrc-open" : ""}`}>
      <div className="lrc-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="lrc-header-left">
          <h3 className="lrc-title">🔁 Loan-Recycling Risk</h3>
          <span className="lrc-tier-chip" style={{ background: `${tierColor}1a`, color: tierColor }}>
            {riskTier}
          </span>
          {!isOpen && (
            <span className="lrc-quick">
              Runway {runwayBand.big} {runwayBand.unit} • {signals.distinctLenders ?? 0} lenders
            </span>
          )}
        </div>
        <span className={`lrc-arrow ${isOpen ? "lrc-arrow-open" : ""}`}>▾</span>
      </div>

      {isOpen && (
        <div className="lrc-body">
          <div className="lrc-subtitle">
            Rule-based analysis on Account Aggregator raw data • Directional band, not a probability •
            Never auto-decisions
          </div>

          {/* Verdict + runway, side by side like the reference mockup */}
          <div className="lrc-verdict" style={{ borderLeftColor: tierColor }}>
            <div className="lrc-verdict-left">
              <div className="lrc-verdict-title">
                {riskTier === "Critical" || riskTier === "High" ? "⚠️ " : ""}Risk tier:{" "}
                <span style={{ color: tierColor }}>{riskTier}</span>
              </div>
              <ul className="lrc-reasons">
                {reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
            <div className="lrc-verdict-right">
              <div className="lrc-runway-label-top">ESTIMATED RUNWAY (directional band)</div>
              <div className="lrc-runway-row">
                <div className="lrc-runway-big" style={{ color: tierColor }}>
                  {runwayBand.big}
                </div>
                <div className="lrc-runway-unit">{runwayBand.unit}</div>
              </div>
              <div className="lrc-bar">
                <i style={{ width: `${runwayBarPct}%`, background: `linear-gradient(90deg, ${tierColor}, #f0a05a)` }} />
              </div>
              <div className="lrc-runway-sub">
                {runwayBand.band} • Confidence: <b>directional</b>
              </div>
              {assumptions && <div className="lrc-assumptions">{assumptions}</div>}
            </div>
          </div>

          {/* Headline signals */}
          <div className="lrc-kpis">
            <div className="lrc-kpi">
              <div className="lrc-kpi-label">Loan-funded-repayment ratio</div>
              <div className="lrc-kpi-val">{ratioPct}%</div>
              <div className="lrc-kpi-foot">
                <span className={`lrc-tag ${ratioTagClass}`}>{ratioTagLabel}</span> new loans ÷ EMIs paid
              </div>
            </div>
            <div className="lrc-kpi">
              <div className="lrc-kpi-label">Avg monthly EMI</div>
              <div className="lrc-kpi-val">{formatCurrency(signals.avgEmiOut)}</div>
              <div className="lrc-kpi-foot">
                vs income {formatCurrency(signals.avgSalaryIn)} · deficit {formatCurrency(signals.monthlyDeficit)}/mo
              </div>
            </div>
            <div className="lrc-kpi">
              <div className="lrc-kpi-label">Distinct lenders</div>
              <div className="lrc-kpi-val">{signals.distinctLenders ?? 0}</div>
              <div className="lrc-kpi-foot" title={lenderFootTitle}>
                {lenderFoot}
              </div>
            </div>
            <div className="lrc-kpi">
              <div className="lrc-kpi-label">Net monthly cash flow</div>
              <div className="lrc-kpi-val" style={{ color: (signals.netCashFlow || 0) < 0 ? "#d1435b" : "#0f9d58" }}>
                {formatCurrency(signals.netCashFlow)}
              </div>
              <div className="lrc-kpi-foot">income − EMIs (excl. new loans)</div>
            </div>
          </div>

          {/* Month-wise obligations table */}
          {monthlySeries.length > 0 && (
            <div className="lrc-table-wrap">
              <div className="lrc-section-title">Loan obligations — month-wise view</div>
              <table className="lrc-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="lrc-num" style={{ color: "#0f9d58" }}>Salary In</th>
                    <th className="lrc-num" style={{ color: "#1e40af" }}>EMI Out</th>
                    <th className="lrc-num" style={{ color: "#166534" }}>Loans In</th>
                    <th className="lrc-num" style={{ color: "#166534" }}>Loans In (Cnt)</th>
                    <th className="lrc-num">True Net</th>
                    <th>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlySeries.map((m) => (
                    <tr
                      key={m.month}
                      className={!m.complete ? "lrc-row-partial" : m.flag === "loan_to_repay" ? "lrc-row-flagged" : ""}
                    >
                      <td>
                        {formatMonth(m.month)}
                        {!m.complete ? " (partial)" : ""}
                      </td>
                      <td className="lrc-num">{formatCurrency(m.salaryIn)}</td>
                      <td className="lrc-num">{formatCurrency(m.emiOut)}</td>
                      <td className="lrc-num">{formatCurrency(m.loanIn)}</td>
                      <td className="lrc-num">{m.disbursalCount}</td>
                      <td className="lrc-num" style={{ color: (m.trueNet || 0) < 0 ? "#d1435b" : "#0f9d58" }}>
                        {formatCurrency(m.trueNet)}
                      </td>
                      <td>
                        {m.flag === "loan_to_repay" && <span className="lrc-flag lrc-flag-red">LOAN-TO-REPAY</span>}
                        {m.flag === "watch" && <span className="lrc-flag lrc-flag-amber">WATCH</span>}
                        {(!m.flag || m.flag === "none") && "—"}
                      </td>
                    </tr>
                  ))}
                  <tr className="lrc-row-total">
                    <td>TOTAL</td>
                    <td className="lrc-num">{formatCurrency(totals.salaryIn)}</td>
                    <td className="lrc-num">{formatCurrency(totals.emiOut)}</td>
                    <td className="lrc-num">{formatCurrency(totals.loanIn)}</td>
                    <td className="lrc-num">{totals.disbursalCount}</td>
                    <td className="lrc-num" style={{ color: totals.trueNet < 0 ? "#d1435b" : "#0f9d58" }}>
                      {formatCurrency(totals.trueNet)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <div className="lrc-footnote">
                True Net = Salary − EMIs (excludes new loans). LOAN-TO-REPAY means new borrowing masked a
                negative true-net month. Partial months are excluded from headline averages above.
              </div>
            </div>
          )}

          {/* The loan-recycling pattern: flow + debt-stacking */}
          <div className="lrc-section-caption">The loan-recycling pattern (month by month)</div>
          <div className="lrc-grid-2">
            <div className="lrc-panel">
              <div className="lrc-panel-title">Loan inflow vs EMI outflow vs salary</div>
              <div className="lrc-panel-sub">
                When EMIs outrun salary and are chased by new loans, the borrower is recycling debt.
              </div>
              <div className="lrc-chart-canvas">
                <Chart type="line" data={flowChartData} options={chartOpts} />
              </div>
              <div className="lrc-legend">
                <span><i className="lrc-dot" style={{ background: "#2563eb" }} />Loan disbursals in</span>
                <span><i className="lrc-dot" style={{ background: "#d1435b" }} />EMI paid out</span>
                <span><i className="lrc-dot" style={{ background: "#0f9d58" }} />Salary in</span>
              </div>
            </div>
            <div className="lrc-panel">
              <div className="lrc-panel-title">
                Debt-stacking acceleration{signals.accelerating ? " ⚠️" : ""}
              </div>
              <div className="lrc-panel-sub">New loans taken per month — acceleration is the leading indicator.</div>
              <div className="lrc-chart-canvas">
                <Chart type="bar" data={stackChartData} options={chartOpts} />
              </div>
              {signals.accelerating && (
                <div className="lrc-footnote">Sharp ramp in new disbursals — a sign of running out of room.</div>
              )}
            </div>
          </div>

          {/* Buffer vs obligations: balance chart + insights */}
          <div className="lrc-section-caption">Buffer vs obligations</div>
          <div className="lrc-grid-2">
            <div className="lrc-panel">
              <div className="lrc-panel-title">Month-end &amp; minimum balance</div>
              <div className="lrc-panel-sub">A thin, fast-draining buffer means cash is passing straight through to EMIs.</div>
              <div className="lrc-chart-canvas">
                <Chart
                  type="bar"
                  data={balChartData}
                  options={{ ...chartOpts, plugins: { legend: { display: true, labels: { boxWidth: 10, font: { size: 10 } } } } }}
                />
              </div>
            </div>
            <div className="lrc-panel">
              <div className="lrc-panel-title">What the signals show</div>
              {reasons.length === 0 ? (
                <div className="lrc-footnote">No significant signals to highlight this period.</div>
              ) : (
                reasons.map((r, i) => (
                  <div className="lrc-insight" key={i}>
                    <div className={`lrc-insight-ico ${INSIGHT_ICONS[i % INSIGHT_ICONS.length]}`}>●</div>
                    <div>{r}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bureau contradiction */}
          {bureauContradiction === true && (
            <div className="lrc-bureau-alert">
              <div className="lrc-bureau-ico">🪪</div>
              <div>
                <div className="lrc-bureau-title">Bureau-contradiction flag: ACTIVE</div>
                <div className="lrc-bureau-body">
                  Bureau likely looks clean because the borrower is servicing every loan — using other loans.
                  A clean bureau plus a {ratioPct}% loan-funded-repayment ratio is more dangerous than a messy
                  one: it's an underwriting trap.
                </div>
              </div>
            </div>
          )}

          {/* Per-lender obligations */}
          {lenders.length > 0 && (
            <div className="lrc-lenders">
              <div className="lrc-section-title">Per-lender obligations (recovered from raw data)</div>
              <table className="lrc-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Category</th>
                    <th className="lrc-num">Avg EMI</th>
                    <th className="lrc-num">Payments seen</th>
                    <th>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {lenders.map((l, i) => {
                    const signal = lenderSignal(l);
                    return (
                      <tr key={i}>
                        <td className="lrc-lender-name">{l.name}</td>
                        <td className="lrc-muted">{l.category}</td>
                        <td className="lrc-num">{formatCurrency(l.avgEmi)}</td>
                        <td className="lrc-num">{l.paymentsSeen}</td>
                        <td>
                          {signal ? <span className={`lrc-flag ${signal.cls}`}>{signal.label}</span> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
